import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { uploadImage, getImageUrl } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';

export const ImageUploader = ({ 
  value = '', 
  onChange, 
  label = 'Imazhi Kryesor',
  multiple = false 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    
    try {
      if (multiple) {
        // Upload multiple files
        const uploadPromises = files.map(file => uploadImage(file));
        const responses = await Promise.all(uploadPromises);
        const newUrls = responses.map(res => getImageUrl(res.data.id));
        onChange([...(value || []), ...newUrls]);
        toast.success(`${files.length} imazhe u ngarkuan me sukses!`);
      } else {
        // Upload single file
        const response = await uploadImage(files[0]);
        const imageUrl = getImageUrl(response.data.id);
        onChange(imageUrl);
        toast.success('Imazhi u ngarkua me sukses!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Gabim gjatë ngarkimit të imazhit');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = (indexOrNull) => {
    if (multiple && typeof indexOrNull === 'number') {
      const newGallery = [...value];
      newGallery.splice(indexOrNull, 1);
      onChange(newGallery);
    } else {
      onChange('');
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-[#0A0A0A]">{label}</label>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload Button */}
      <div 
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={`
          border-2 border-dashed border-[#E4E4E7] rounded-lg p-6 
          flex flex-col items-center justify-center gap-2 
          cursor-pointer hover:border-[#D4B982] hover:bg-[#F4F4F5]/50 
          transition-all duration-200
          ${isUploading ? 'opacity-50 cursor-wait' : ''}
        `}
      >
        {isUploading ? (
          <>
            <Loader2 size={32} className="text-[#D4B982] animate-spin" />
            <span className="text-sm text-[#71717A]">Duke ngarkuar...</span>
          </>
        ) : (
          <>
            <Upload size={32} className="text-[#71717A]" />
            <span className="text-sm text-[#71717A]">
              {multiple ? 'Kliko për të zgjedhur imazhe' : 'Kliko për të zgjedhur imazhin'}
            </span>
            <span className="text-xs text-[#A1A1AA]">
              PNG, JPG, WEBP deri në 5MB
            </span>
          </>
        )}
      </div>

      {/* Preview - Single Image */}
      {!multiple && value && (
        <div className="relative inline-block">
          <img 
            src={value} 
            alt="Preview" 
            className="w-32 h-24 object-cover rounded-lg border border-[#E4E4E7]"
          />
          <button
            type="button"
            onClick={() => handleRemove()}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Preview - Multiple Images (Gallery) */}
      {multiple && value && value.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {value.map((url, index) => (
            <div key={index} className="relative">
              <img 
                src={url} 
                alt={`Gallery ${index + 1}`} 
                className="w-24 h-18 object-cover rounded-lg border border-[#E4E4E7]"
              />
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Gallery count */}
      {multiple && value && value.length > 0 && (
        <p className="text-xs text-[#71717A]">
          {value.length} {value.length === 1 ? 'imazh' : 'imazhe'} të ngarkuara
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
